import { AllocationType } from '../../projects/components/project-member-allocation-grid';
import { IProjectMembersAllocationData } from '../../projects/type';
import { UserRoleAppFormat } from './enum';

export function formatStatus(status: string): string {
  const words = status.toLowerCase().split('_');
  if (status === 'ONGOING') {
    return 'Executing';
  }

  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function statusMap(projectStatus: string): {
  status: string;
  colorCode: string;
} {
  if (['DISCONTINUED', 'COMPLETED'].includes(projectStatus)) {
    return { status: 'Inactive', colorCode: '#F69C31' };
  }
  if (['ONGOING', 'INITIATED', 'HYPERCARE_SUPPORT'].includes(projectStatus)) {
    return { status: 'Active', colorCode: '#06BF3A' };
  }
  return { status: 'Unknown', colorCode: 'gray' };
}

export const isSameDate = (date1: Date, date2: Date): boolean => {
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
};

export const isOperationsLeadAndSuperAdmin = (
  roles: string[] | undefined
): boolean => {
  if (roles) {
    return (
      roles.includes(UserRoleAppFormat.OperationsLead) &&
      roles.includes(UserRoleAppFormat.SuperAdmin)
    );
  }
  return false;
};

export const isOperationsLeadOrSuperAdminOrAdmin = (
  roles: string[] | undefined
): boolean => {
  if (roles) {
    return (
      roles.includes(UserRoleAppFormat.OperationsLead) ||
      roles.includes(UserRoleAppFormat.SuperAdmin) ||
      roles.includes(UserRoleAppFormat.Admin)
    );
  }
  return false;
};

export const isOperationsLead = (roles: string[] | undefined): boolean => {
  if (roles) {
    return roles.includes(UserRoleAppFormat.OperationsLead);
  }
  return false;
};

export const calculateTimeRemaining = (tentativeEndDate: Date): string => {
  const today = new Date();
  const endDate = new Date(tentativeEndDate);

  const timeDifference = endDate.getTime() - today.getTime();
  const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  if (daysRemaining < 0) {
    return 'Concluded';
  }

  if (daysRemaining >= 7) {
    const weeksRemaining = Math.floor(daysRemaining / 7);
    const remainingDays = daysRemaining % 7;

    if (weeksRemaining > 0) {
      if (remainingDays > 0) {
        return `${weeksRemaining}W ${remainingDays}D Left`;
      }
      return `${weeksRemaining} ${weeksRemaining > 1 ? 'Weeks' : 'Week'} Left`;
    }
  }

  return `${daysRemaining} ${daysRemaining === 1 ? 'Day' : 'Days'} Left`;
};

export const suggestedAllocationCalculate = (
  unit: number,
  value: string,
  hoursPerWeek: string
): number => {
  let suggestedAllocationValue =
    Math.round(unit * Number(hoursPerWeek) * 10) / 10;
  suggestedAllocationValue =
    suggestedAllocationValue === 0
      ? suggestedAllocationValue + Number(hoursPerWeek)
      : suggestedAllocationValue;
  suggestedAllocationValue =
    Number(value) === 40 ? 0 : suggestedAllocationValue;

  return suggestedAllocationValue;
};

export const suggestedAllocationListGenerate = (
  members: IProjectMembersAllocationData[],
  unit: number,
  value: string,
  isMaximumValue: boolean
): AllocationType[] => {
  const totalCurrentAllocationHours =
    calculateTotalCurrentAllocationHours(members);
  const suggestedAllocationList: AllocationType[] = members.map(
    (member: IProjectMembersAllocationData) => {
      const suggestedAllocationValue = suggestedAllocationCalculate(
        unit,
        value,
        member.hoursPerWeek
      );
      return {
        projectId: member.projectId,
        projectMemberId: member.id,
        suggestedAllocation:
          isMaximumValue && Number(value) + totalCurrentAllocationHours >= 40
            ? member.hoursPerWeek
            : suggestedAllocationValue.toString(),
        currentAllocation: member.hoursPerWeek,
        projectName: member.projectName,
        colorCode: member.colorCode
      };
    }
  );
  return suggestedAllocationList;
};

export const calculateTotalCurrentAllocationHours = (
  members: IProjectMembersAllocationData[]
): number => {
  return members
    .map((member) => Number(member.hoursPerWeek))
    .reduce((a, b) => a + b, 0);
};
